import React from 'react';
import { Download } from 'lucide-react';
import { formatClock, formatMultiple, formatShareCount, shortName, splitAsOn } from '../../utils/ipoSubscription';

const StatCard = ({ tone, label, value, valueTone = 'text-slate-900', note, noteTone = 'text-slate-500' }) => (
  <li className={`rounded-xl border p-4 ${tone}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1.5 text-xl sm:text-2xl font-extrabold tracking-tight truncate ${valueTone}`}>{value}</p>
    <p className={`mt-0.5 text-[11px] font-semibold truncate ${noteTone}`}>{note}</p>
  </li>
);

const retailMood = (avg) => {
  if (avg === null) return 'No retail data yet';
  if (avg >= 10) return 'High retail enthusiasm';
  return avg >= 1 ? 'Healthy retail interest' : 'Muted retail demand';
};

const DEFAULT_DESCRIPTION = (
  <>
    Real-time demand for IPOs during the bidding window on BSE and NSE, showing how many times each issue has been
    subscribed across <strong className="text-slate-700">Qualified Institutional Buyers (QIB)</strong>,{' '}
    <strong className="text-slate-700">Non-Institutional (sNII &amp; bNII)</strong> and{' '}
    <strong className="text-slate-700">Retail</strong> categories.
  </>
);

export const SubscriptionHeaderCard = ({
  heading,
  stats,
  lastSync,
  year,
  onExport,
  canExport,
  description = DEFAULT_DESCRIPTION,
  feedBadge = 'BSE & NSE Consolidated',
  totalLabel = 'Total Active Issues',
}) => {
  const sync = lastSync ? splitAsOn(lastSync) : null;
  const [closing, ...moreClosing] = stats.closingToday;

  return (
    <section className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Direct Exchange Feed{sync ? `: Last sync ${sync.date}, ${formatClock(sync.time)} IST` : ''}
        </span>
        <span className="px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold text-slate-600">
          {feedBadge}
        </span>
      </div>

      <div className="mt-3 flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="max-w-3xl">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{heading}</h1>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">

          <button
            type="button"
            onClick={onExport}
            disabled={!canExport}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-emerald-300 bg-emerald-50 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard
          tone="bg-white border-slate-200"
          label={totalLabel}
          value={`${stats.total} Issues`}
          note={`${stats.liveCount} open for bidding today`}
          noteTone="text-[#008080]"
        />
        <StatCard
          tone="bg-sky-50/70 border-sky-200"
          label="Highest Subscription"
          value={stats.top ? `${formatMultiple(stats.top.total)}x` : '—'}
          valueTone="text-sky-700"
          note={
            stats.top
              ? `${shortName(stats.top.company)}${stats.top.sharesBid ? ` (${formatShareCount(stats.top.sharesBid)} shares bid)` : ''}`
              : 'No bids yet'
          }
          noteTone="text-sky-700"
        />
        <StatCard
          tone="bg-emerald-50/70 border-emerald-200"
          label="Average Retail Demand"
          value={stats.avgRetail !== null ? `${formatMultiple(stats.avgRetail)}x` : '—'}
          valueTone="text-emerald-700"
          note={retailMood(stats.avgRetail)}
          noteTone="text-emerald-700"
        />
        <StatCard
          tone="bg-orange-50/80 border-orange-200"
          label="Closing at 5 PM Today"
          value={closing ? shortName(closing.company) : 'No closings today'}
          valueTone="text-orange-700"
          note={
            closing
              ? `${typeof closing.total === 'number' ? `${formatMultiple(closing.total)}x total subscription` : 'Bidding closes at 5:00 PM'}${moreClosing.length ? ` · +${moreClosing.length} more` : ''
              }`
              : 'Check the IPO calendar for dates'
          }
          noteTone="text-orange-700"
        />
      </ul>
    </section>
  );
};

export default SubscriptionHeaderCard;
